import { formatLogActor } from './logActor';
import { wrapLogBold } from './logFormat';
import { roleLabelFromPermission } from 'pages/Settings/redux';

const LIVE_DEMO_SESSION_LOG_ID = 'live_demo_session_open';
/** Seeded showcase activityLogs go stale between reseeds — freshen when lag exceeds this. */
const LIVE_DEMO_STALE_MS = 12 * 60 * 60 * 1000;

type LogLike = {
  id: string
  type: string
  message: string
  date: string
  value: string
  updatedAsOf: string
  created_by?: string | null
};

const readLoginTimeIso = (): string | undefined => {
  try {
    return localStorage.getItem('login_time') || undefined;
  } catch {
    return undefined;
  }
};

/**
 * FE-only: if newest seeded log is >12h behind login, shift the whole series
 * so the newest lands on login time (Today / metrics stay current). Never writes DB.
 */
const withFreshenedLiveDemoDates = <T extends LogLike>(logs: T[], anchorIso?: string): T[] => {
  if (!logs.length) return logs;
  const anchorMs = +(anchorIso ? new Date(anchorIso) : new Date());
  if (!Number.isFinite(anchorMs)) return logs;

  let newestMs = -Infinity;
  for (let i = 0; i < logs.length; i += 1) {
    const t = +new Date(logs[i].date);
    if (Number.isFinite(t) && t > newestMs) newestMs = t;
  }
  if (!Number.isFinite(newestMs) || newestMs === -Infinity) return logs;

  const lagMs = anchorMs - newestMs;
  // Already current enough (or ahead) — leave seed timestamps alone.
  if (lagMs <= LIVE_DEMO_STALE_MS) return logs;

  return logs.map((log) => {
    const dateMs = +new Date(log.date);
    const updatedMs = +new Date(log.updatedAsOf || log.date);
    return {
      ...log,
      date: Number.isFinite(dateMs)
        ? new Date(dateMs + lagMs).toISOString()
        : log.date,
      updatedAsOf: Number.isFinite(updatedMs)
        ? new Date(updatedMs + lagMs).toISOString()
        : log.updatedAsOf,
    };
  });
};

const buildLiveDemoSessionLog = ({
  staffId,
  username,
  permission,
  at,
}: {
  staffId?: string | null
  username?: string | null
  permission?: number | null
  at?: string
}): LogLike => {
  const date = at || new Date().toISOString();
  const role = roleLabelFromPermission(Number(permission));
  return {
    id: LIVE_DEMO_SESSION_LOG_ID,
    type: 'Staff logged in',
    message: `${formatLogActor(staffId, username)} ${wrapLogBold(`(${role})`)} opened live demo (view-only)`,
    date,
    value: 'N/A',
    updatedAsOf: date,
    created_by: staffId || null,
  };
};

/** Session-only live demo open-activity (not written to activity_logs). */
export const withLiveDemoSessionLog = <T extends LogLike>(
  logs: T[],
  liveView: boolean,
  actor: {
    staffId?: string | null
    username?: string | null
    permission?: number | null
  },
): T[] => {
  const withoutSession = logs.filter((log) => String(log.id) !== LIVE_DEMO_SESSION_LOG_ID);
  if (!liveView) return withoutSession;
  const at = readLoginTimeIso();
  // Anchor to login_time so every getLogs in the session shifts by the same delta.
  const freshened = withFreshenedLiveDemoDates(withoutSession, at);
  return [buildLiveDemoSessionLog({ ...actor, at }) as T, ...freshened];
};
